import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import LanguageSelector from "@/components/LanguageSelector";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { direction } = useLanguage(); 
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        const isLoggedIn = !!data.session;
        setIsAuthenticated(isLoggedIn);
        
        if (isLoggedIn) {
          console.log("User already logged in, redirecting to dashboard");
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: direction === 'rtl' ? "שגיאה" : "Error",
        description: direction === 'rtl' ? "הסיסמאות אינן תואמות" : "Passwords do not match"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // First check if registration is enabled
      const { data: settingsData, error: settingsError } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'registration_open')
        .single();

      if (settingsError) {
        console.error('Error checking registration status:', settingsError);
        throw new Error('Unable to verify registration status');
      }

      const isRegistrationOpen = settingsData?.setting_value !== false;

      if (!isRegistrationOpen) {
        throw new Error(direction === 'rtl' 
          ? 'ההרשמה למערכת סגורה כרגע' 
          : 'Registration is currently closed');
      }

      // Attempt to sign up the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'member', // Default role for new users
            is_active: true
          }
        }
      });
      
      if (error) throw error;
      
      if (data?.user) {
        // Create user role entry
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: data.user.id,
            role: 'member',
            is_active: true,
            created_at: new Date().toISOString()
          });

        if (roleError) {
          console.error('Error creating user role:', roleError);
          // Don't throw here, as the user is already created
        }

        // Store user profile with email
        const { error: profileError } = await supabase
          .from('user_settings')
          .insert({
            user_id: data.user.id,
            settings_key: 'user_profile',
            settings_value: { email }
          });

        if (profileError) {
          console.error('Error saving user profile:', profileError);
          // Don't throw here, as the user is already created
        }
      }
      
      toast({
        title: direction === 'rtl' ? "הרשמה מוצלחת" : "Registration Successful",
        description: direction === 'rtl' 
          ? "החשבון נוצר בהצלחה. אנא התחבר עם פרטי החשבון החדש שלך." 
          : "Account created successfully. Please login with your new credentials."
      });
      
      // Redirect to login page after successful registration
      navigate("/login", { 
        state: { 
          message: direction === 'rtl' 
            ? "החשבון נוצר בהצלחה. אנא התחבר עם פרטי החשבון החדש שלך." 
            : "Account created successfully. Please login with your new credentials."
        }
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      toast({
        variant: "destructive",
        title: direction === 'rtl' ? "שגיאת הרשמה" : "Registration Failed",
        description: error.message || (direction === 'rtl' 
          ? "אירעה שגיאה בתהליך ההרשמה" 
          : "An error occurred during registration")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  const goToHomepage = () => {
    navigate('/');
  };

  // If checking auth status, show loader
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-synagogue-light">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-synagogue-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg">{direction === 'rtl' ? "טוען..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  // If already authenticated but still on register page, redirect immediately to dashboard
  if (isAuthenticated) {
    navigate("/dashboard", { replace: true });
    return (
      <div className="min-h-screen flex items-center justify-center bg-synagogue-light">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-synagogue-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg">{direction === 'rtl' ? "כבר מחובר, מעביר..." : "Already logged in, redirecting..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center bg-synagogue-light p-4 ${direction === 'rtl' ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={goToHomepage}
            className="text-synagogue-blue hover:text-synagogue-gold transition-colors"
          >
            {direction === 'rtl' ? "← חזרה לדף הבית" : "← Back to Homepage"}
          </button>
          <LanguageSelector />
        </div>
        
        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-synagogue-blue">
              {direction === 'rtl' ? "הרשמה למערכת" : "Register"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="email">{direction === 'rtl' ? "דואר אלקטרוני" : "Email"}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={direction === 'rtl' ? "הכנס דואר אלקטרוני" : "Enter your email"}
                  required
                  className="text-synagogue-blue"
                  autoComplete="new-password"
                  name={`email-${Math.random().toString(36).substr(2, 9)}`}
                  data-form-type="other"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{direction === 'rtl' ? "סיסמה" : "Password"}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={direction === 'rtl' ? "הכנס סיסמה" : "Enter your password"}
                  required
                  className="text-synagogue-blue"
                  autoComplete="new-password"
                  name={`password-${Math.random().toString(36).substr(2, 9)}`}
                  data-form-type="other"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{direction === 'rtl' ? "אימות סיסמה" : "Confirm Password"}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={direction === 'rtl' ? "הכנס שוב את הסיסמה" : "Confirm your password"}
                  required
                  className="text-synagogue-blue"
                  autoComplete="new-password"
                  name={`confirm-password-${Math.random().toString(36).substr(2, 9)}`}
                  data-form-type="other"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-synagogue-gold hover:bg-synagogue-blue text-white" 
                disabled={isLoading}
              >
                {isLoading 
                  ? (direction === 'rtl' ? "מבצע הרשמה..." : "Registering...") 
                  : (direction === 'rtl' ? "הרשמה" : "Register")}
              </Button>

              <div className="text-center mt-4">
                <p className="text-gray-500">
                  {direction === 'rtl' ? "כבר יש לך חשבון?" : "Already have an account?"}{" "}
                  <button
                    type="button"
                    onClick={goToLogin}
                    className="text-synagogue-blue hover:text-synagogue-gold"
                  >
                    {direction === 'rtl' ? "התחבר כאן" : "Login here"}
                  </button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register; 